import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle, X, Camera, ImageSquare, Tag } from "@phosphor-icons/react";

/**
 * Bulk photo tray for field techs.
 * Shows every job photo that hasn't been assigned to a checklist line yet.
 * Tap a photo → picker → tap a checklist line to assign.
 *
 * Props:
 *   job: the job dict (with .checklist, .before_photos, .after_photos, .photos)
 *   onReload: () => Promise<void> to refresh after assignment
 *   baseUrl: REACT_APP_BACKEND_URL (from api)
 */
export default function BulkPhotoTray({ job, onReload, baseUrl = "" }) {
  const [picking, setPicking] = useState(null); // { path }
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [savingSuggestion, setSavingSuggestion] = useState(false);

  const token = typeof window !== "undefined" ? (localStorage.getItem("access_token") || "") : "";
  const withAuth = (path) => `${baseUrl}/api/files/${path}?auth=${encodeURIComponent(token)}`;

  const unassigned = useMemo(() => {
    const out = [];
    ["before_photos", "after_photos", "photos"].forEach((b) => {
      for (const p of (job?.[b] || [])) {
        if (isDict(p) && !p.checklist_id && !p.inspection_room) {
          out.push({ ...p, _bucket: b });
        }
      }
    });
    return out;
  }, [job]);

  const linePhotoCounts = useMemo(() => {
    const c = {};
    ["before_photos", "after_photos", "photos"].forEach((b) => {
      for (const p of (job?.[b] || [])) {
        if (isDict(p) && p.checklist_id) {
          c[p.checklist_id] = (c[p.checklist_id] || 0) + 1;
        }
      }
    });
    return c;
  }, [job]);

  const checklist = job?.checklist || [];

  const assign = async (checklistId) => {
    if (!picking) return;
    setBusy(true);
    const line = checklist.find((c) => c.id === checklistId);
    try {
      await api.post(`/jobs/${job.id}/photos/assign-line`, {
        path: picking.path,
        checklist_id: checklistId,
      });
      toast.success("Tagged to line");
      const path = picking.path;
      setPicking(null);
      await onReload?.();
      // Kick off AI suggestion in the background — silent failures are OK.
      try {
        const r = await api.post(`/jobs/${job.id}/photos/ai-suggest-line`, {
          path,
          line_text: line?.text || "",
        });
        if (r.data?.condition || r.data?.note) {
          setSuggestion({
            condition: r.data.condition || "",
            note: r.data.note || "",
            tags: r.data.tags || [],
            line_id: checklistId,
            line_text: line?.text || "",
            path,
          });
        }
      } catch (_) {
        // silent
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const acceptSuggestion = async () => {
    if (!suggestion) return;
    setSavingSuggestion(true);
    try {
      await api.patch(`/jobs/${job.id}/checklist/${suggestion.line_id}/inspection`, {
        condition: suggestion.condition || undefined,
        notes: suggestion.note || undefined,
      });
      toast.success(`Applied: ${suggestion.condition || "—"}`);
      setSuggestion(null);
      await onReload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSuggestion(false);
    }
  };

  if (unassigned.length === 0 && checklist.length === 0) return null;

  return (
    <div className="mt-4 rugged-card p-4" data-testid="bulk-photo-tray">
      <div className="flex items-center gap-2 mb-3">
        <Camera weight="fill" size={16} className="text-[#00C4CC]" />
        <h3 className="font-display font-black uppercase tracking-widest text-xs">Bulk Photo Tray</h3>
        <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
          {unassigned.length} untagged
        </span>
      </div>

      {unassigned.length > 0 && (
        <>
          <p className="text-xs text-slate-600 mb-3">
            Take photos in bulk first, then tap each one below to tag it to a checklist line. Each line needs at least 2 photos before it can be marked done.
          </p>
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="bulk-photo-tray-photos">
            {unassigned.map((p, i) => (
              <button
                key={p.path || i}
                type="button"
                onClick={() => setPicking({ path: p.path })}
                data-testid={`bulk-photo-tray-photo-${i}`}
                className="relative h-24 w-24 flex-shrink-0 border-2 border-[#0f172a] hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#00C4CC]"
              >
                <img src={withAuth(p.path)} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] font-black uppercase tracking-widest py-0.5 text-center">
                  Tap to tag
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Live per-line photo counter (progress toward the 2-photo minimum) */}
      {checklist.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="bulk-photo-tray-line-counters">
          {checklist.map((item) => {
            const ct = linePhotoCounts[item.id] || 0;
            const ok = ct >= 2;
            return (
              <div key={item.id} className="flex items-center gap-2 text-xs" data-testid={`line-photo-count-${item.id}`}>
                {ok ? <CheckCircle weight="fill" size={14} className="text-emerald-600 flex-shrink-0" />
                    : <ImageSquare size={14} className="text-slate-400 flex-shrink-0" />}
                <span className="flex-1 truncate">{item.text}</span>
                <span className={`text-[10px] font-black uppercase tracking-widest ${ok ? "text-emerald-700" : "text-slate-500"}`}>
                  {ct}/2 photos
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Picker overlay */}
      {picking && (
        <div
          className="fixed inset-0 z-[140] bg-black/70 flex items-end sm:items-center justify-center p-4"
          data-testid="bulk-photo-picker"
          onClick={(e) => { if (e.target === e.currentTarget) setPicking(null); }}
        >
          <div className="bg-white w-full max-w-md border-2 border-[#0f172a] shadow-2xl">
            <div className="bg-[#0f172a] text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tag weight="fill" size={18} className="text-[#00C4CC]" />
                <div className="font-display font-black uppercase tracking-widest text-sm">Tag Photo To Line</div>
              </div>
              <button onClick={() => setPicking(null)} data-testid="bulk-photo-picker-close" className="p-1 hover:bg-white/10">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-4">
              <img src={withAuth(picking.path)} alt="" className="w-full max-h-48 object-contain border-2 border-slate-200 mb-3" />
              {checklist.length === 0 ? (
                <div className="text-xs text-slate-500 italic">No checklist lines on this job.</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="bulk-photo-picker-options">
                  {checklist.map((item) => {
                    const ct = linePhotoCounts[item.id] || 0;
                    return (
                      <button
                        key={item.id}
                        disabled={busy}
                        onClick={() => assign(item.id)}
                        data-testid={`bulk-photo-picker-line-${item.id}`}
                        className="w-full text-left px-3 py-2 border-2 border-slate-300 hover:border-[#0f172a] hover:bg-slate-50 flex items-center gap-2 text-sm disabled:opacity-40"
                      >
                        <span className={`h-5 w-5 flex items-center justify-center text-[10px] font-black text-white ${ct >= 2 ? "bg-emerald-600" : "bg-slate-400"}`}>{ct}</span>
                        <span className="flex-1">{item.text}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestion modal — pops after a photo is tagged */}
      {suggestion && (
        <div
          className="fixed inset-0 z-[145] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="ai-suggestion-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setSuggestion(null); }}
        >
          <div className="bg-white w-full max-w-md border-2 border-[#0f172a] shadow-2xl">
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">✨</span>
                <div className="font-display font-black uppercase tracking-widest text-sm">ProMan Suggests</div>
              </div>
              <button onClick={() => setSuggestion(null)} data-testid="ai-suggestion-dismiss" className="p-1 hover:bg-white/10">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-5">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Line</div>
              <div className="text-sm font-bold mb-3">{suggestion.line_text}</div>

              {suggestion.condition && (
                <>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Suggested Condition</div>
                  <div className={`inline-block px-3 py-1.5 text-xs font-black uppercase tracking-widest text-white mb-3 ${
                    suggestion.condition === "great" ? "bg-emerald-600" :
                    suggestion.condition === "good" ? "bg-sky-600" :
                    suggestion.condition === "fair" ? "bg-amber-500" :
                    suggestion.condition === "poor" ? "bg-red-600" :
                    suggestion.condition === "broken" ? "bg-red-800" :
                    "bg-slate-400"
                  }`} data-testid="ai-suggestion-condition">
                    {suggestion.condition}
                  </div>
                </>
              )}

              {suggestion.note && (
                <>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Suggested Note</div>
                  <div className="text-sm text-slate-700 border-2 border-slate-200 bg-slate-50 p-3 italic mb-3" data-testid="ai-suggestion-note">
                    "{suggestion.note}"
                  </div>
                </>
              )}

              {suggestion.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {suggestion.tags.map((t, i) => (
                    <span key={i} className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 bg-violet-100 text-violet-800">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t-2 border-slate-200 px-4 py-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                data-testid="ai-suggestion-skip"
                className="h-10 px-4 text-[11px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 border-2 border-slate-300"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={acceptSuggestion}
                disabled={savingSuggestion}
                data-testid="ai-suggestion-accept"
                className="h-10 px-5 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40"
              >
                {savingSuggestion ? "Saving…" : "Accept — One Tap"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function isDict(x) { return x && typeof x === "object" && !Array.isArray(x); }
